import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Headphones } from 'lucide-react';
import BookCard from './BookCard';
import { Button } from './ui/button';

const BookRow = ({ title, books, isAudiobookRow = false, viewCounts = {} }) => {
  const rowRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScroll = useRef(0);
  const dragMoved = useRef(false);

  // Defensive dedupe — never render the same book twice in a single row
  // (avoids "Encountered two children with the same key" React warning).
  const uniqueBooks = React.useMemo(() => {
    if (!Array.isArray(books)) return [];
    const seen = new Set();
    return books.filter((b) => {
      const id = b?.id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [books]);

  const onMouseDown = (e) => {
    if (!rowRef.current) return;
    isDragging.current = true;
    dragMoved.current = false;
    dragStartX.current = e.pageX - rowRef.current.offsetLeft;
    dragStartScroll.current = rowRef.current.scrollLeft;
    rowRef.current.style.cursor = 'grabbing';
    rowRef.current.style.userSelect = 'none';
  };
  const onMouseMove = (e) => {
    if (!isDragging.current || !rowRef.current) return;
    e.preventDefault();
    const x = e.pageX - rowRef.current.offsetLeft;
    const walk = (x - dragStartX.current) * 1.4;
    if (Math.abs(walk) > 5) dragMoved.current = true;
    rowRef.current.scrollLeft = dragStartScroll.current - walk;
  };
  const onMouseUp = () => {
    if (!rowRef.current) return;
    isDragging.current = false;
    rowRef.current.style.cursor = 'grab';
    rowRef.current.style.userSelect = '';
  };
  // Suppress click navigation when user actually dragged
  const onClickCapture = (e) => {
    if (dragMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      dragMoved.current = false;
    }
  };

  const checkArrows = () => {
    if (rowRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = rowRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (rowRef.current) {
      const scrollAmount = direction === 'left' ? -800 : 800;
      rowRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(checkArrows, 300);
    }
  };

  React.useEffect(() => {
    checkArrows();
    const handleResize = () => checkArrows();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [books]);

  if (!books || books.length === 0) return null;

  return (
    <div className="relative group mb-8">
      {/* Title */}
      <h2 className={`text-2xl font-bold mb-4 px-4 sm:px-6 lg:px-8 flex items-center ${isAudiobookRow ? 'text-purple-300' : 'text-white'}`}>
        {isAudiobookRow && <Headphones className="h-6 w-6 mr-2 text-purple-400" />}
        {title}
        {isAudiobookRow && (
          <span className="ml-3 text-xs font-normal bg-purple-600/30 text-purple-300 px-2 py-1 rounded-full">
            Listen Now
          </span>
        )}
      </h2>

      {/* Scroll Container */}
      <div className="relative px-4 sm:px-6 lg:px-8">
        {/* Left Arrow */}
        {showLeftArrow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => scroll('left')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 h-full w-12 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-none ${isAudiobookRow ? 'bg-purple-900/50' : 'bg-black/50'}`}
          >
            <ChevronLeft className="h-10 w-10" />
          </Button>
        )}

        {/* Right Arrow */}
        {showRightArrow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => scroll('right')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 h-full w-12 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-none ${isAudiobookRow ? 'bg-purple-900/50' : 'bg-black/50'}`}
          >
            <ChevronRight className="h-10 w-10" />
          </Button>
        )}

        {/* Books Grid */}
        <div
          ref={rowRef}
          onScroll={checkArrows}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onClickCapture={onClickCapture}
          className="flex space-x-2 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', cursor: 'grab' }}
        >
          {uniqueBooks.map((book) => (
            <BookCard
              key={book.id}
              book={book}
              isAudiobook={isAudiobookRow || book.is_audiobook}
              viewCount={viewCounts[book.id]?.views || 0}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BookRow;
